/**
 * makeCameraBacking — adapts a runtime to the entity-addressed {@link CameraBacking} that
 * `engine.camera` consumes. orthographicSize get/set reads/writes the Camera2D component data
 * (and bumps the component-state version so the renderer re-reads, like CameraRigSystem does);
 * isActive/setActive defer to the runtime's active-camera selection via injected callbacks.
 */

import type { EntityHandle } from "./IEngineContext";
import type { CameraBacking } from "./modules/CameraScriptApi";
import type { GameObject } from "../GameObject";

const CAMERA = "core.Camera2D";

/** Bump a component instance's version so per-frame consumers (renderer) re-read its data. */
function bumpVersion(go: GameObject, typeId: string): void {
  const id = go.getComponent(typeId);
  if (id == null) return;
  const st = go.getComponentState(id);
  if (st) st.version = (st.version ?? 0) + 1;
}

export function makeCameraBacking(
  resolve: (entity: EntityHandle) => GameObject | null,
  getActiveGuid: () => string | null,
  requestActive?: (guid: string) => void,
  screenToWorld?: CameraBacking["screenToWorld"],
): CameraBacking {
  return {
    getOrthographicSize: (e) => {
      const go = resolve(e);
      const d = go?.getComponentData(CAMERA) as { orthographicSize?: number } | null | undefined;
      return d?.orthographicSize ?? 0;
    },
    setOrthographicSize: (e, size) => {
      const go = resolve(e);
      if (!go) return;
      const d = go.getComponentData(CAMERA) as { orthographicSize?: number } | null;
      if (d) {
        d.orthographicSize = size;
        bumpVersion(go, CAMERA);
      }
    },
    isActive: (e) => {
      const go = resolve(e);
      return !!go && getActiveGuid() === go.guid;
    },
    setActive: requestActive
      ? (e) => {
          const go = resolve(e);
          if (go) requestActive(go.guid);
        }
      : undefined,
    screenToWorld,
  };
}
